"use client";

import Loading from '@/components/loading';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Building2,
  Check,
  ChevronDown,
  Code2,
  CreditCard,
  Download,
  FileText,
  HeadphonesIcon,
  LayoutDashboard,
  KeyRound,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Moon,
  Package,
  QrCode,
  Settings,
  ShieldCheck,
  Store,
  Sun,
  Ticket,
  Undo2,
  User,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/ThemeContext";
import { useGetAllowedOrganizations } from "@/hooks/useOrganizations";
import {
  ROLES,
  NAVIGATION_ITEMS,
  getUserRole,
  logout,
  type NavigationItem,
} from "@/lib/auth";
import { getRoleConfig } from "@/lib/roleConfig";
import { getAllMachines } from "@/services/machine/useMachineService";
import { getAllOrganizations } from "@/services/organization/organizationService";
import { getAllShops } from "@/services/shop/useShopService";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePWAInstall } from "./install";
import { setOrganizationPermission } from "@/services/userPermission/userPermissionService";

// Icon mapping
const iconMap = {
  LayoutDashboard,
  Building2,
  Users,
  Store,
  Settings,
  CreditCard,
  Code2,
  HeadphonesIcon,
  FileText,
  MapPin,
  Activity,
  BarChart3,
  User,
  AlertCircle,
  Undo2,
  ShieldCheck,
  QrCode,
  KeyRound,
  Lock,
  Tickets: Ticket,
  WashingMachine: Package, // Fallback to Package if WashingMachine doesn't exist
};

export function NavBar() {
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [selectedOrgName, setSelectedOrgName] = useState<string>(
    "Select Organization",
  );
  const [userRole, setUserRole] = useState<
    (typeof ROLES)[keyof typeof ROLES] | null
  >(null);
  const [configVersion, setConfigVersion] = useState(0);
  const hasAutoSelectedSingleOrgRef = useRef(false);

  const { canInstall, install } = usePWAInstall();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { isDarkMode, toggleTheme } = useTheme();

  // Fetch organizations
  const { data: organizationsDataRaw, isLoading: isLoadingOrganizations } =
    useGetAllowedOrganizations();
  const organizationsData = Array.isArray(organizationsDataRaw)
    ? organizationsDataRaw
    : [];

  // Prefetch data for likely next pages when hovering over navigation items
  const prefetchPageData = (url: string) => {
    if (url === "/dashboard") {
      // Dashboard might need shops and machines data
      queryClient.prefetchQuery({
        queryKey: ["shops", "all"],
        queryFn: getAllShops,
      });
      queryClient.prefetchQuery({
        queryKey: ["machines", "all"],
        queryFn: getAllMachines,
      });
    } else if (url === "/shop") {
      // Prefetch shops data
      queryClient.prefetchQuery({
        queryKey: ["shops", "all"],
        queryFn: getAllShops,
      });
    } else if (url === "/machine") {
      // Prefetch machines, shops, and machine models
      queryClient.prefetchQuery({
        queryKey: ["machines", "all"],
        queryFn: getAllMachines,
      });
      queryClient.prefetchQuery({
        queryKey: ["shops", "all"],
        queryFn: getAllShops,
      });
    } else if (url === "/organization") {
      // Prefetch organizations
      queryClient.prefetchQuery({
        queryKey: ["organizations", "all"],
        queryFn: getAllOrganizations,
      });
    }
  };

  // Load selected organization and user role from localStorage
  useEffect(() => {
    const savedOrg = localStorage.getItem("selectedOrganization");
    const savedOrgName = localStorage.getItem("selectedOrganizationName");
    if (savedOrg && savedOrgName) {
      setSelectedOrg(savedOrg);
      setSelectedOrgName(savedOrgName);
    }

    // Load initial user role
    const role = getUserRole();
    setUserRole(role);
  }, []);

  // Listen for userRole changes in localStorage - immediate updates
  useEffect(() => {
    const checkUserRole = () => {
      const role = getUserRole();
      if (role !== userRole) {
        setUserRole(role);
      }
    };

    // Check on mount
    checkUserRole();

    // Listen for storage changes (when localStorage is updated in another tab/component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "userRole") {
        checkUserRole();
      }
    };

    // Listen for custom storage event (when localStorage is updated in same tab)
    const handleCustomStorageChange = () => {
      checkUserRole();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userRoleChanged", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userRoleChanged", handleCustomStorageChange);
    };
  }, [userRole]);

  // Re-compute nav items whenever the dynamic route config loads or changes
  useEffect(() => {
    const onConfigChanged = () => setConfigVersion((v) => v + 1);
    window.addEventListener("routeConfigChanged", onConfigChanged);
    return () => window.removeEventListener("routeConfigChanged", onConfigChanged);
  }, []);

  // Update selected organization name when organizations data changes
  useEffect(() => {
    if (selectedOrg && organizationsData.length > 0) {
      const currentOrg = organizationsData.find(
        (org) => String(org.name) === selectedOrg,
      );
      if (currentOrg && currentOrg.organizationname !== selectedOrgName) {
        // Organization name has changed, update it
        setSelectedOrgName(currentOrg.organizationname);
        localStorage.setItem(
          "selectedOrganizationName",
          currentOrg.organizationname,
        );
      }
    }
  }, [organizationsData, selectedOrg]);

  // Automatically select the only allowed organization when the user has access to one organization.
  useEffect(() => {
    if (organizationsData.length === 1) {
      const singleOrg = organizationsData[0];
      const singleOrgId = String(singleOrg.name);
      const singleOrgName = singleOrg.organizationname || singleOrgId;

      if (!hasAutoSelectedSingleOrgRef.current || selectedOrg !== singleOrgId) {
        hasAutoSelectedSingleOrgRef.current = true;
        void handleOrgSelect(singleOrgId, singleOrgName);
      }
      return;
    }

    if (organizationsData.length === 0) {
      hasAutoSelectedSingleOrgRef.current = false;
      setOrgDialogOpen(false);
      return;
    }

    hasAutoSelectedSingleOrgRef.current = false;
  }, [organizationsData, selectedOrg]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleOrgSelect = async (orgId: string, orgName: string) => {
    setSelectedOrg(orgId);
    setSelectedOrgName(orgName);
    localStorage.setItem("selectedOrganization", orgId);
    localStorage.setItem("selectedOrganizationName", orgName);
    setOrgDialogOpen(false);

    try {
      await setOrganizationPermission(orgId);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["shops"] }),
        queryClient.invalidateQueries({ queryKey: ["machines"] }),
      ]);
    } catch (err) {
      console.error("Gagal set organization permission:", err);
    }

    window.dispatchEvent(new Event("selectedOrganizationChanged"));
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  // Get filtered navigation items based on current user role.
  // When on merchant routes, use MERCHANT as fallback so sidebar shows merchant links
  // (otherwise null/initial state would show admin menu and clicks would redirect back).
  const currentUserRole = useMemo(() => {
    if (userRole) return userRole;
    if (typeof pathname === "string" && pathname.startsWith("/merchant"))
      return ROLES.MERCHANT;
    return ROLES.ADMIN;
  }, [userRole, pathname]);
  const menuItems = useMemo(() => {
    if (!currentUserRole) return [];
    const config = getRoleConfig();
    return NAVIGATION_ITEMS.filter((item) => {
      if (config && config.route_permissions.length > 0) {
        const sorted = [...config.route_permissions].sort((a, b) => b.route.length - a.route.length);
        const match = sorted.find(
          (rp) => item.url === rp.route || item.url.startsWith(rp.route + "/"),
        );
        if (match) {
          return match.allowed_roles.includes(currentUserRole);
        }
      }
      // Route not in dynamic config — use the static allowedRoles from NAV_ITEMS
      return (item.allowedRoles as string[]).includes(currentUserRole);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserRole, configVersion]);

  const isItemActive = (url: string) => {
    if (url === "/") return pathname === "/";
    if (url === "/dashboard") return pathname === "/dashboard"; // Exact match for dashboard
    return pathname.startsWith(url) && pathname !== "/dashboard"; // Starts with for others
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen &&
        event.target &&
        !(event.target as HTMLElement).closest(".mobile-menu-content")
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  // Mobile tabs to show
  const mobileTabs = menuItems.filter((item: NavigationItem) => item.mobileTab);
  const otherItems = menuItems.filter(
    (item: NavigationItem) => !item.mobileTab,
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <Tooltip.Provider delayDuration={100}>
        <Sidebar
          collapsible="icon"
          className="hidden md:flex border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
        >
          <SidebarHeader className="p-4 space-y-3 border-b border-sidebar-border">
            <div
              className={`flex ${
                isCollapsed
                  ? "flex-col items-center gap-2"
                  : "items-center gap-3"
              }`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-sidebar-accent dark:bg-sidebar-accent/50">
                <Image
                  src={isDarkMode ? "/sh_logo_dark.png" : "/sh_logo_light.png"}
                  alt="logo"
                  width={32}
                  height={32}
                />
              </div>

              {!isCollapsed && (
                <div className="flex-1">
                  <h2 className="font-semibold text-sm text-sidebar-foreground">
                    SHLaundritek
                  </h2>
                </div>
              )}
            </div>

            {/* Organization Picker - shown for admin and merchant */}
            {!isCollapsed && (
              <div className="space-y-1">
                {organizationsData.length > 1 && (
                  <p className="text-xs text-muted-foreground px-2">Pick Org</p>
                )}
                <Button
                  variant="outline"
                  disabled={organizationsData.length === 1}
                  className={`w-full justify-between h-auto py-2 px-3 border-gray-200 dark:border-gray-600 bg-transparent ${
                    organizationsData.length === 1
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:bg-sidebar-accent dark:hover:bg-sidebar-accent"
                  }`}
                  onClick={() => {
                    if (organizationsData.length === 1) {
                      const singleOrg = organizationsData[0];
                      void handleOrgSelect(
                        String(singleOrg.name),
                        singleOrg.organizationname || String(singleOrg.name),
                      );
                      return;
                    }

                    if (organizationsData.length > 1) {
                      setOrgDialogOpen(true);
                      return;
                    }

                    console.warn("No organizations available for selection.");
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 shrink-0" />
                    <span className="text-sm truncate">{selectedOrgName}</span>
                  </div>
                  {organizationsData.length > 1 && (
                    <ChevronDown className="w-4 h-4 shrink-0 ml-2" />
                  )}
                </Button>
              </div>
            )}

            {/* Collapsed Organization Picker - Icon Only */}
            {isCollapsed && (
              <div className="ml-[-8px]">
                <Tooltip.Root disableHoverableContent={!isCollapsed}>
                  <Tooltip.Trigger asChild>
                    <SidebarMenuItem className="list-none m-0 p-0">
                      <SidebarMenuButton
                        disabled={organizationsData.length === 1}
                        onClick={() => {
                          if (organizationsData.length === 1) {
                            const singleOrg = organizationsData[0];
                            void handleOrgSelect(
                              String(singleOrg.name),
                              singleOrg.organizationname || String(singleOrg.name),
                            );
                            return;
                          }

                          if (organizationsData.length > 1) {
                            setOrgDialogOpen(true);
                            return;
                          }

                          console.warn("No organizations available for selection.");
                        }}
                        className={`w-full justify-center [&>svg]:text-sidebar-foreground ${
                          organizationsData.length === 1
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer hover:bg-sidebar-accent dark:hover:bg-sidebar-accent"
                        }`}
                      >
                        <Building2 className="w-4 h-4" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="right"
                      sideOffset={10}
                      className="z-50 overflow-hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs text-gray-50 animate-in fade-in-0 zoom-in-95"
                    >
                      {selectedOrgName}
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </div>
            )}
          </SidebarHeader>

          <SidebarContent className="px-2">
            <SidebarMenu>
              {menuItems.map((item: NavigationItem) => (
                <Tooltip.Root
                  key={item.url}
                  disableHoverableContent={!isCollapsed}
                >
                  <Tooltip.Trigger asChild>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        className={`w-full ${
                          isCollapsed ? "justify-center" : "justify-start"
                        } ${
                          isItemActive(item.url)
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        }`}
                      >
                        <Link
                          href={item.url}
                          onMouseEnter={() => prefetchPageData(item.url)}
                          className={`flex items-center ${
                            isCollapsed ? "justify-center" : "gap-3"
                          } p-2 rounded-md`}
                        >
                          {React.createElement(
                            iconMap[item.icon as keyof typeof iconMap] ||
                              Package,
                            {
                              className: `w-4 h-4 ${
                                isItemActive(item.url)
                                  ? "text-sidebar-accent-foreground"
                                  : "text-sidebar-foreground/80"
                              }`,
                            },
                          )}
                          {!isCollapsed && (
                            <span
                              className={
                                isItemActive(item.url) ? "font-medium" : ""
                              }
                            >
                              {item.title}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </Tooltip.Trigger>
                  {isCollapsed && (
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="right"
                        sideOffset={10}
                        className="z-50 overflow-hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs text-gray-50 animate-in fade-in-0 zoom-in-95"
                      >
                        {item.title}
                        <Tooltip.Arrow className="fill-gray-900" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  )}
                </Tooltip.Root>
              ))}
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter className="p-2">
            <Tooltip.Root disableHoverableContent={!isCollapsed}>
              <Tooltip.Trigger asChild>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleLogout}
                      className={`w-full ${
                        isCollapsed ? "justify-center" : "justify-start"
                      } hover:bg-muted cursor-pointer`}
                    >
                      <div
                        className={`flex items-center ${
                          isCollapsed ? "justify-center" : "gap-3"
                        } p-2 rounded-md`}
                      >
                        <LogOut className="w-4 h-4 text-sidebar-foreground/80" />
                        {!isCollapsed && <span>Logout</span>}
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </Tooltip.Trigger>
              {isCollapsed && (
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="right"
                    sideOffset={10}
                    className="z-50 overflow-hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs text-gray-50 animate-in fade-in-0 zoom-in-95"
                  >
                    Logout
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              )}
            </Tooltip.Root>
          </SidebarFooter>
        </Sidebar>
      </Tooltip.Provider>

      {/* Mobile Bottom Navigation - Hide on merchant routes */}
      {!pathname?.startsWith("/merchant") && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-sidebar border-t border-sidebar-border z-50 transition-colors duration-200">
          <div className="flex justify-around">
            {mobileTabs.map((item: NavigationItem) => (
              <Link
                key={item.url}
                href={item.url}
                onMouseEnter={() => prefetchPageData(item.url)}
                className={`flex flex-col items-center justify-center p-3 w-full ${
                  isItemActive(item.url)
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                {React.createElement(
                  iconMap[item.icon as keyof typeof iconMap] || Package,
                  {
                    className: "w-5 h-5",
                  },
                )}
                <span className="text-xs mt-1">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile Top Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-sidebar border-b border-sidebar-border z-50 p-3 flex justify-between items-center transition-colors duration-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8  rounded-lg flex items-center justify-center">
            <Image
              src={isDarkMode ? "/sh_logo_dark.png" : "/sh_logo_light.png"}
              alt="logo"
              width={32}
              height={32}
              priority={true}
            />
          </div>
          <h2 className="font-semibold text-sm">SHLaundribek</h2>
        </div>

        <button onClick={toggleMobileMenu} className="p-2 text-foreground">
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {/* Mobile Menu (Full-screen overlay) - theme & logout inside navbar menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-content md:hidden fixed inset-0 bg-sidebar z-40 pt-16 pb-24 overflow-y-auto transition-colors duration-200">
          <div className="p-4 space-y-2">
            {/* Organization Picker - Mobile */}
            <div className="space-y-1 pb-3 mb-3 border-b border-gray-200 dark:border-gray-700">
              {organizationsData.length > 1 && (
                <p className="text-xs text-muted-foreground px-1">Pick Org</p>
              )}
              <Button
                variant="outline"
                disabled={organizationsData.length === 1}
                className={`w-full justify-between h-auto py-3 px-3 ${
                  organizationsData.length === 1
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => {
                  if (organizationsData.length === 1) {
                    const singleOrg = organizationsData[0];
                    void handleOrgSelect(
                      String(singleOrg.name),
                      singleOrg.organizationname || String(singleOrg.name),
                    );
                    return;
                  }

                  if (organizationsData.length > 1) {
                    setOrgDialogOpen(true);
                    return;
                  }

                  console.warn("No organizations available for selection.");
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm truncate">{selectedOrgName}</span>
                </div>
                {organizationsData.length > 1 && (
                  <ChevronDown className="w-4 h-4 flex-shrink-0 ml-2" />
                )}
              </Button>
            </div>

            {/* Theme toggle - at top of navbar menu so always visible */}
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg text-foreground hover:bg-muted transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                {isDarkMode ? (
                  <Sun className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <Moon className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{isDarkMode ? "Light mode" : "Dark mode"}</span>
              </div>
              <Switch
                checked={isDarkMode}
                onCheckedChange={() => toggleTheme()}
                aria-label={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              />
            </div>

            {/* Install App - only shown inside navbar menu */}
            {canInstall && (
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  install();
                }}
                className="flex items-center gap-3 p-3 rounded-lg text-foreground w-full text-left hover:bg-muted transition-colors"
              >
                <Download className="w-5 h-5 flex-shrink-0" />
                <span>Install App</span>
              </button>
            )}

            {otherItems.map((item: NavigationItem) => (
              <Link
                key={item.url}
                href={item.url}
                onMouseEnter={() => prefetchPageData(item.url)}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  isItemActive(item.url)
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                    : "text-foreground"
                }`}
              >
                {React.createElement(
                  iconMap[item.icon as keyof typeof iconMap] || Package,
                  {
                    className: "w-5 h-5",
                  },
                )}
                <span>{item.title}</span>
              </Link>
            ))}

            {/* Logout - in navbar menu */}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              className="flex items-center gap-3 p-3 rounded-lg text-foreground w-full text-left hover:bg-muted transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Organization Picker Dialog */}
      <Dialog open={orgDialogOpen && organizationsData.length > 1} onOpenChange={setOrgDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Pick Organization</DialogTitle>
            <DialogDescription>
              Select an organization to filter your data and settings.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
            {isLoadingOrganizations ? (
              <div className="flex items-center justify-center py-8">
                <Loading size={32} />
              </div>
            ) : organizationsData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No organizations found
              </div>
            ) : (
              organizationsData.map((org) => (
                <Button
                  key={org.name}
                  variant={
                    selectedOrg === String(org.name) ? "default" : "ghost"
                  }
                  className="w-full justify-between h-auto py-3 px-4"
                  onClick={() =>
                    handleOrgSelect(String(org.name), org.organizationname)
                  }
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5" />
                    <div className="text-left">
                      <p className="font-medium">{org.organizationname}</p>
                      {org.status && (
                        <p className="text-xs text-muted-foreground">
                          Status: {org.status}
                        </p>
                      )}
                    </div>
                  </div>
                  {selectedOrg === String(org.name) && (
                    <Check className="w-5 h-5" />
                  )}
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
